import { motion } from 'framer-motion';
import { Flame } from 'lucide-react';
import { cn } from '@/lib/utils';

const RED = new Set([1, 3, 5, 7, 9, 12, 14, 16, 18, 19, 21, 23, 25, 27, 30, 32, 34, 36]);
const WHEEL_ORDER = [0, 32, 15, 19, 4, 21, 2, 25, 17, 34, 6, 27, 13, 36, 11, 30, 8, 23, 10, 5, 24, 16, 33, 1, 20, 14, 31, 9, 22, 18, 29, 7, 28, 12, 35, 3, 26];
const STEP = 360 / 37;
const numColor = (n: number): 'green' | 'red' | 'black' => (n === 0 ? 'green' : RED.has(n) ? 'red' : 'black');

// Panel grande encima de la ruleta: los números calientes se encienden uno a uno.
export function FireNumbersPanel({ fireNumbers, litFire, mega, igniting }: {
  fireNumbers: number[]; litFire: number[]; mega: boolean; igniting: boolean;
}) {
  if (fireNumbers.length === 0) return null;
  const litSet = new Set(litFire);
  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
      className="relative overflow-hidden rounded-2xl border-2 border-orange-500/40 bg-gradient-to-b from-orange-950/70 to-black/70 p-4 shadow-[0_0_30px_rgba(249,115,22,0.35)]"
    >
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_50%_120%,rgba(249,115,22,0.4),transparent_60%)]" />
      <div className="relative mb-3 flex items-center justify-center gap-2">
        <Flame className="h-5 w-5 text-orange-400" />
        <span className="font-headline text-lg font-black uppercase tracking-widest text-transparent bg-clip-text bg-gradient-to-r from-yellow-200 via-orange-400 to-red-500">
          Números de Fuego
        </span>
        {mega && (
          <motion.span animate={{ scale: [1, 1.15, 1] }} transition={{ duration: 0.7, repeat: Infinity }}
            className="rounded-full bg-gradient-to-r from-red-600 to-orange-500 px-2 py-0.5 text-[0.6rem] font-black text-white shadow">MEGA 🔥</motion.span>
        )}
      </div>
      <div className="relative flex flex-wrap justify-center gap-2">
        {fireNumbers.map((n) => {
          const lit = litSet.has(n);
          return (
            <motion.div key={n}
              initial={false}
              animate={lit ? { scale: [0.4, 1.25, 1], rotate: [0, -8, 0] } : { scale: 0.85, opacity: 0.25 }}
              transition={{ duration: 0.45, type: 'spring', stiffness: 260, damping: 12 }}
              className="relative">
              {lit && (
                <motion.span className="absolute -top-3 left-1/2 -translate-x-1/2 text-xl"
                  animate={{ y: [-2, -7, -2], opacity: [0.7, 1, 0.7] }} transition={{ duration: 0.8, repeat: Infinity }}>🔥</motion.span>
              )}
              <div className={cn('grid h-11 w-11 place-items-center rounded-xl border-2 text-lg font-black transition-all',
                lit
                  ? 'border-orange-300 bg-gradient-to-b from-yellow-300 via-orange-500 to-red-600 text-white shadow-[0_0_18px_rgba(249,115,22,0.95)]'
                  : 'border-white/10 bg-black/40 text-white/40')}>
                {n}
              </div>
            </motion.div>
          );
        })}
      </div>
      {igniting && <p className="relative mt-3 text-center text-xs font-bold uppercase tracking-widest text-orange-300/80 animate-pulse">Encendiendo números…</p>}
    </motion.div>
  );
}

// Ruleta Mega Fire: obsidiana ardiente, sectores calientes en llamas, brasas y aro de fuego.
export function MegaFireWheel({ rotation, ballRotation, spinning, result, fireNumbers = [], igniting = false }: {
  rotation: number; ballRotation: number; spinning: boolean; result: number | null; fireNumbers?: number[]; igniting?: boolean;
}) {
  const cx = 160, cy = 160, R = 158;
  const rSector = 130, rText = 113, rTrack = 142, rBall = 142;
  const fire = new Set(fireNumbers);
  const pointAt = (a: number, rad: number) => [cx + rad * Math.sin((a * Math.PI) / 180), cy - rad * Math.cos((a * Math.PI) / 180)];

  return (
    <div className="relative mx-auto" style={{ width: 320, height: 320 }}>
      {/* Halo de fuego */}
      <motion.div className="absolute inset-0 rounded-full bg-orange-600/30 blur-2xl"
        animate={{ opacity: igniting ? [0.4, 0.8, 0.4] : [0.25, 0.45, 0.25] }} transition={{ duration: 1.2, repeat: Infinity }} />

      {/* Llamas girando alrededor del aro */}
      <motion.div className="absolute inset-0" animate={{ rotate: 360 }} transition={{ duration: 14, repeat: Infinity, ease: 'linear' }}>
        {Array.from({ length: 16 }).map((_, i) => {
          const a = (i * 360) / 16;
          const [fx, fy] = pointAt(a, R + 2);
          return <span key={i} className="absolute -translate-x-1/2 -translate-y-1/2 text-base" style={{ left: fx, top: fy }}>🔥</span>;
        })}
      </motion.div>

      {/* Puntero de fuego */}
      <div className="absolute left-1/2 top-[-12px] z-30 -translate-x-1/2 text-2xl drop-shadow-[0_2px_6px_rgba(0,0,0,0.9)]">🔥</div>

      {/* Aro exterior obsidiana */}
      <svg width={320} height={320} viewBox="0 0 320 320" className="absolute inset-0">
        <defs>
          <radialGradient id="mfRim" cx="50%" cy="35%" r="70%">
            <stop offset="0%" stopColor="#7c2d12" /><stop offset="55%" stopColor="#3f1208" /><stop offset="100%" stopColor="#0a0a0a" />
          </radialGradient>
          <radialGradient id="mfCore" cx="45%" cy="35%" r="75%">
            <stop offset="0%" stopColor="#fde68a" /><stop offset="40%" stopColor="#f97316" /><stop offset="75%" stopColor="#b91c1c" /><stop offset="100%" stopColor="#1a0a05" />
          </radialGradient>
        </defs>
        <circle cx={cx} cy={cy} r={R} fill="url(#mfRim)" />
        <circle cx={cx} cy={cy} r={R - 13} fill="#070707" />
      </svg>

      {/* Disco giratorio */}
      <motion.svg width={320} height={320} viewBox="0 0 320 320" animate={{ rotate: rotation }}
        transition={{ duration: spinning ? 5 : 0, ease: [0.16, 0.74, 0.1, 1] }} className="absolute inset-0" style={{ transformOrigin: '50% 50%' }}>
        <circle cx={cx} cy={cy} r={rTrack} fill="#120907" stroke="#7c2d12" strokeWidth={1.5} />
        {WHEEL_ORDER.map((n, i) => {
          const a0 = i * STEP, a1 = (i + 1) * STEP;
          const [x0, y0] = pointAt(a0, rSector); const [x1, y1] = pointAt(a1, rSector); const [tx, ty] = pointAt(a0 + STEP / 2, rText);
          const isFire = fire.has(n);
          const fill = isFire ? '#ea580c' : numColor(n) === 'green' ? '#15803d' : numColor(n) === 'red' ? '#7f1d1d' : '#141414';
          return (
            <g key={i}>
              <path d={`M ${cx} ${cy} L ${x0} ${y0} A ${rSector} ${rSector} 0 0 1 ${x1} ${y1} Z`} fill={fill} stroke={isFire ? '#fdba74' : '#3f1208'} strokeWidth={isFire ? 1.4 : 0.6} />
              <text x={tx} y={ty} fill={isFire ? '#fff7ed' : '#fca5a5'} fontSize={isFire ? 11 : 9} fontWeight={isFire ? 900 : 700} textAnchor="middle" dominantBaseline="middle" transform={`rotate(${a0 + STEP / 2}, ${tx}, ${ty})`}>{n}</text>
            </g>
          );
        })}
        <circle cx={cx} cy={cy} r={60} fill="url(#mfCore)" stroke="#7c2d12" strokeWidth={2} />
        <circle cx={cx} cy={cy} r={26} fill="#1a0a05" />
        <circle cx={cx} cy={cy} r={10} fill="#fde68a" />
      </motion.svg>

      {/* Bola */}
      <motion.div className="absolute inset-0 z-20" animate={{ rotate: ballRotation }} transition={{ duration: spinning ? 5 : 0, ease: [0.16, 0.74, 0.1, 1] }} style={{ transformOrigin: '50% 50%' }}>
        <div className="absolute left-1/2 -translate-x-1/2 rounded-full" style={{
          top: cx - rBall - 4, width: 13, height: 13,
          background: 'radial-gradient(circle at 35% 30%, #fff, #fdba74 60%, #ea580c)',
          boxShadow: '0 0 8px rgba(249,115,22,0.9), 0 2px 4px rgba(0,0,0,0.6)',
        }} />
      </motion.div>

      {/* Resultado */}
      {result !== null && !spinning && (
        <div className="absolute inset-0 z-30 flex items-center justify-center">
          <motion.div initial={{ scale: 0, rotate: -20 }} animate={{ scale: 1, rotate: 0 }} transition={{ type: 'spring', stiffness: 260, damping: 16 }}
            className={cn('grid h-[72px] w-[72px] place-items-center rounded-full border-[3px] text-3xl font-black text-white shadow-[0_0_28px_rgba(0,0,0,0.7)]',
              fire.has(result) ? 'border-orange-200 bg-gradient-to-b from-orange-500 to-red-600 shadow-[0_0_30px_rgba(249,115,22,0.9)]'
                : numColor(result) === 'green' ? 'border-green-200 bg-green-600' : numColor(result) === 'red' ? 'border-red-200 bg-red-700' : 'border-zinc-400 bg-zinc-900')}>
            {result}
          </motion.div>
        </div>
      )}
    </div>
  );
}
